import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Wind } from "lucide-react";
import { dojo } from "@/plugins/vibeDojo/dojoAudio";
import { useGuide } from "@/plugins/vibeDojo/useGuide";
import { Typewriter } from "@/plugins/vibeDojo/Typewriter";

const SESSION_SECONDS = 60;
const PHASE_MS = 4000; // inhale / hold / exhale each 4s → 12s cycle
const PHASES = [
  { key: "in", label: "Breathe in…", scale: 1.22 },
  { key: "hold", label: "Hold…", scale: 1.22 },
  { key: "out", label: "Exhale…", scale: 1.0 },
];

export function BreathingSession({ onClose }) {
  const { name, image } = useGuide();
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [remaining, setRemaining] = useState(SESSION_SECONDS);

  useEffect(() => {
    dojo.startBreathing();
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setPhaseIdx(Math.floor((elapsed % (PHASE_MS * 3)) / PHASE_MS));
      const left = Math.max(0, SESSION_SECONDS - Math.floor(elapsed / 1000));
      setRemaining(left);
      if (elapsed >= SESSION_SECONDS * 1000) close();
    }, 150);
    return () => { clearInterval(tick); dojo.stopBreathing(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => { onClose?.(); };
  const phase = PHASES[phaseIdx];
  const progress = ((SESSION_SECONDS - remaining) / SESSION_SECONDS) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-[#050b09]/95 backdrop-blur-xl text-emerald-50"
        data-testid="breathing-session"
      >
        {/* glow that pulses with the breath */}
        <motion.div
          className="absolute h-[520px] w-[520px] rounded-full bg-emerald-500/10 blur-3xl"
          animate={{ scale: phase.scale, opacity: phase.key === "out" ? 0.35 : 0.7 }}
          transition={{ duration: PHASE_MS / 1000, ease: "easeInOut" }}
        />

        <button onClick={close} data-testid="breathing-exit"
          className="absolute top-6 right-6 grid place-items-center h-10 w-10 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors">
          <X className="h-5 w-5" />
        </button>

        <p className="relative text-[11px] uppercase tracking-[0.35em] text-emerald-300/80 mb-6" data-testid="breathing-guide-name">
          {name} breathes with you
        </p>

        {/* Guide portrait — inhale/exhale pulse zoom */}
        <motion.div
          className="relative h-52 w-52 sm:h-64 sm:w-64 rounded-full overflow-hidden ring-2 ring-emerald-400/40 shadow-[0_0_60px_-10px_rgba(16,185,129,0.6)]"
          animate={{ scale: phase.scale }}
          transition={{ duration: PHASE_MS / 1000, ease: "easeInOut" }}
          data-testid="breathing-portrait"
        >
          <img src={image} alt={name} className="h-full w-full object-cover" />
        </motion.div>

        {/* Timed instruction typewriter */}
        <div className="relative mt-10 h-10 text-center">
          <Typewriter key={`${phaseIdx}-${Math.floor((SESSION_SECONDS - remaining) / 12)}`}
            text={phase.label} speed={55}
            className="font-display text-3xl sm:text-4xl font-light tracking-tight text-emerald-100" />
        </div>

        {/* Progress + countdown */}
        <div className="relative mt-8 w-64 max-w-[80vw]">
          <div className="h-1 bg-white/10 overflow-hidden rounded-full">
            <div className="h-full bg-emerald-400/80 transition-all duration-150 ease-linear" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-center gap-2 text-xs text-emerald-200/70" data-testid="breathing-countdown">
            <Wind className="h-3.5 w-3.5" /> {remaining}s of stillness remaining
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
